import './OrdersPage.scss'

import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import {
    X,
    CaretDown,
    DownloadSimple,
    DotsThreeVertical,
    MagnifyingGlass,
    Gear,
    FunnelSimple,
    ClipboardText,
    Receipt,
    MinusCircle,
    ArrowCounterClockwise,
    CheckCircle,
    Warning,
    Circle,
    Info,
    Wallet,
    Bank,
    CreditCard,
} from '@phosphor-icons/react'

// ─── Status workflow ──────────────────────────────────────────────────────────

const STATUS_FLOW = {
  pending:   { next: 'submitted', nextLabel: null },
  submitted: { next: 'confirmed', nextLabel: 'Confirm order' },
  paid:      { next: 'confirmed', nextLabel: 'Confirm order' },
  confirmed: { next: 'ready',     nextLabel: 'Mark as ready' },
  ready:     { next: 'completed', nextLabel: 'Complete order' },
  completed: { next: null,        nextLabel: null },
  cancelled: { next: null,        nextLabel: null },
}

const REVERT_MAP = {
  confirmed: 'submitted',
  ready:     'confirmed',
  completed: 'ready',
}

const PAYMENT_LOCKED = new Set(['paid', 'cancelled'])

const CANCEL_REASONS = [
  'Item out of stock',
  'Customer requested cancellation',
  'Duplicate order',
  'Store closing early',
  'Unable to fulfil order',
  'Other',
]

// ─── Data ─────────────────────────────────────────────────────────────────────

const stats = [
  { label: 'OPEN ORDERS',        value: '5' },
  { label: 'CLOSED TODAY',       value: '42' },
  { label: 'TOTAL ORDERS TODAY', value: '47' },
  { label: 'REVENUE TODAY',      value: 'RM 3,847.00' },
]

const INITIAL_ORDERS = [
  {
    id: '#ORD-2025-04821',
    customer: 'Bernard Liew',
    phone: '+60103456789',
    points: 32,
    orderedAt: '6 May 2026 11:02 AM',
    items: [
      { qty: 2, name: 'Salmon Mentai Roll', modifiers: ['Standard spice', 'Extra mentaiko mayo +RM2.00'], price: 57.80 },
      { qty: 1, name: 'Yuzu Lemonade', modifiers: [], price: 9.90 },
    ],
    customerNote: 'Extra wasabi on the side please. No raw garlic.',
    subtotal: 67.70,
    voucher: { label: 'Voucher applied (10% off your order)', amount: -8.77 },
    serviceTax: 5.89,
    sst: 3.54,
    total: 68.54,
    payment: { method: 'FPX online banking', icon: 'bank', paidAt: '6 May 2026 11:06 AM', status: 'paid' },
    type: 'Dine-in', placed: '11:53 AM', closed: '-',
    status: 'confirmed', payment_status: 'paid', pos_status: 'open',
  },
  {
    id: '#ORD-2025-04820',
    customer: 'Xavier Liew',
    phone: '+60 12-345 6789',
    points: 28,
    orderedAt: '6 May 2026 11:02 AM',
    items: [
      { qty: 1, name: 'Chicken Karaage Plate', modifiers: [], price: 42.00 },
      { qty: 1, name: 'Miso Soup with Rice', modifiers: [], price: 18.00 },
    ],
    customerNote: null,
    subtotal: 60.00,
    voucher: null,
    serviceTax: 6.00,
    sst: 3.60,
    total: 69.60,
    payment: { method: 'E-wallet', icon: 'wallet', paidAt: '6 May 2026 11:06 AM', status: 'paid' },
    type: 'Pickup', placed: '11:53 AM', closed: '-',
    status: 'submitted', payment_status: 'not_paid', pos_status: null,
  },
  {
    id: '#ORD-2025-04819',
    customer: 'Amir Hassan',
    phone: '+60112233445',
    points: 20,
    orderedAt: '6 May 2026 11:00 AM',
    items: [
      { qty: 2, name: 'Chicken Katsu Don', modifiers: [], price: 52.00 },
      { qty: 1, name: 'Yuzu Lemonade', modifiers: [], price: 9.90 },
    ],
    customerNote: null,
    subtotal: 61.90,
    voucher: null,
    serviceTax: 6.19,
    sst: 3.71,
    total: 71.80,
    payment: { method: 'FPX online banking', icon: 'bank', paidAt: '6 May 2026 11:04 AM', status: 'paid' },
    type: 'Dine-in', placed: '11:53 AM', closed: '-',
    status: 'paid', payment_status: 'paid', pos_status: 'open',
  },
  {
    id: '#ORD-2025-04818',
    customer: 'Siti Rahimah',
    phone: '+60198887766',
    points: 25,
    orderedAt: '6 May 2026 11:20 AM',
    items: [
      { qty: 2, name: 'Salmon Mentai Roll', modifiers: [], price: 57.80 },
      { qty: 1, name: 'Yuzu Lemonade', modifiers: [], price: 9.90 },
    ],
    customerNote: null,
    subtotal: 67.70,
    voucher: { label: 'Voucher applied (RICE5)', amount: -5.00 },
    serviceTax: 6.27,
    sst: 3.76,
    total: 72.73,
    payment: { method: 'E-wallet', icon: 'wallet', paidAt: '6 May 2026 11:24 AM', status: 'paid' },
    type: 'Kiosk', placed: '11:20 AM', closed: '-',
    status: 'ready', payment_status: 'paid', pos_status: 'open',
  },
  {
    id: '#ORD-2025-04817',
    customer: 'Lee Chee Wai',
    phone: '+60166554433',
    points: 18,
    orderedAt: '6 May 2026 11:09 AM',
    items: [
      { qty: 1, name: 'Beef Teppanyaki Plate', modifiers: [], price: 41.20 },
      { qty: 1, name: 'Cola', modifiers: [], price: 6.50 },
    ],
    customerNote: null,
    subtotal: 47.70,
    voucher: null,
    serviceTax: 4.77,
    sst: 2.86,
    total: 55.33,
    payment: { method: 'FPX online banking', icon: 'bank', paidAt: null, status: 'pending' },
    type: 'Pickup', placed: '11:09 AM', closed: '-',
    status: 'pending', payment_status: 'not_paid', pos_status: null,
  },
  {
    id: '#ORD-2025-04816',
    customer: 'Ryan Loh Jee Feng',
    phone: '+60127774488',
    points: 35,
    orderedAt: '6 May 2026 11:02 AM',
    items: [
      { qty: 2, name: 'Unagi Don', modifiers: ['Extra rice +RM2.00'], price: 65.80 },
    ],
    customerNote: null,
    subtotal: 65.80,
    voucher: { label: 'Voucher applied (RICE5)', amount: -5.00 },
    serviceTax: 6.08,
    sst: 3.65,
    total: 70.53,
    payment: { method: 'E-wallet', icon: 'wallet', paidAt: '6 May 2026 11:06 AM', status: 'paid' },
    type: 'Pickup', placed: '10:55 AM', closed: '11:14 AM',
    status: 'completed', payment_status: 'paid', pos_status: 'closed',
  },
]

// ─── Mock order_status_histories  (mirrors DB: id, order_id, status, note, created_at) ──

const ORDER_STATUS_HISTORIES = {
  '#ORD-2025-04821': [
    { id: 4, status: 'confirmed', note: null,  created_at: '6 May 2026, 11:05 AM' },
    { id: 3, status: 'paid',      note: null,  created_at: '6 May 2026, 11:04 AM' },
    { id: 2, status: 'submitted', note: null,  created_at: '6 May 2026, 11:03 AM' },
    { id: 1, status: 'pending',   note: null,  created_at: '6 May 2026, 11:02 AM' },
  ],
  '#ORD-2025-04820': [
    { id: 2, status: 'submitted', note: null,  created_at: '6 May 2026, 11:53 AM' },
    { id: 1, status: 'pending',   note: null,  created_at: '6 May 2026, 11:52 AM' },
  ],
  '#ORD-2025-04819': [
    { id: 3, status: 'paid',      note: null,  created_at: '6 May 2026, 11:04 AM' },
    { id: 2, status: 'submitted', note: null,  created_at: '6 May 2026, 11:01 AM' },
    { id: 1, status: 'pending',   note: null,  created_at: '6 May 2026, 11:00 AM' },
  ],
  '#ORD-2025-04818': [
    { id: 4, status: 'ready',     note: null,  created_at: '6 May 2026, 11:26 AM' },
    { id: 3, status: 'confirmed', note: null,  created_at: '6 May 2026, 11:25 AM' },
    { id: 2, status: 'submitted', note: null,  created_at: '6 May 2026, 11:21 AM' },
    { id: 1, status: 'pending',   note: null,  created_at: '6 May 2026, 11:20 AM' },
  ],
  '#ORD-2025-04817': [
    { id: 1, status: 'pending',   note: null,  created_at: '6 May 2026, 11:09 AM' },
  ],
  '#ORD-2025-04816': [
    { id: 6, status: 'completed', note: null,  created_at: '6 May 2026, 11:14 AM' },
    { id: 5, status: 'ready',     note: null,  created_at: '6 May 2026, 11:10 AM' },
    { id: 4, status: 'confirmed', note: null,  created_at: '6 May 2026, 11:07 AM' },
    { id: 3, status: 'paid',      note: null,  created_at: '6 May 2026, 11:06 AM' },
    { id: 2, status: 'submitted', note: null,  created_at: '6 May 2026, 11:03 AM' },
    { id: 1, status: 'pending',   note: null,  created_at: '6 May 2026, 11:02 AM' },
  ],
}

// ─── Mock order_pos_histories  (mirrors DB: id, order_pos_id, old_status, status, action, ref, remark, system_log, created_at) ──

const ORDER_POS_HISTORIES = {
  '#ORD-2025-04821': [
    { id: 2, old_status: 'submitted', status: 'confirmed', action: 'MERCHANT_ACCEPT', ref: 'POS-001', remark: null,                        system_log: 'Auto-accepted via merchant portal', created_at: '6 May 2026, 11:05 AM' },
    { id: 1, old_status: null,        status: 'submitted', action: 'ORDER_CREATED',   ref: 'POS-001', remark: null,                        system_log: 'Order pushed to POS',               created_at: '6 May 2026, 11:03 AM' },
  ],
  '#ORD-2025-04818': [
    { id: 3, old_status: 'confirmed', status: 'open',      action: 'MARK_READY',      ref: 'POS-001', remark: null,                        system_log: 'Kitchen marked ready',              created_at: '6 May 2026, 11:26 AM' },
    { id: 2, old_status: 'open',      status: 'open',      action: 'PAYMENT_SUCCESS', ref: 'TXN-991', remark: 'E-wallet payment confirmed', system_log: 'Payment webhook received',          created_at: '6 May 2026, 11:24 AM' },
    { id: 1, old_status: null,        status: 'open',      action: 'ORDER_CREATED',   ref: 'POS-001', remark: null,                        system_log: 'Order pushed to POS',               created_at: '6 May 2026, 11:21 AM' },
  ],
  '#ORD-2025-04816': [
    { id: 5, old_status: 'ready',     status: 'closed',    action: 'ORDER_COMPLETE',  ref: 'POS-001', remark: null,                        system_log: 'Order marked complete',             created_at: '6 May 2026, 11:14 AM' },
    { id: 4, old_status: 'confirmed', status: 'open',      action: 'MARK_READY',      ref: 'POS-001', remark: null,                        system_log: 'Kitchen marked ready',              created_at: '6 May 2026, 11:10 AM' },
    { id: 3, old_status: 'submitted', status: 'confirmed', action: 'MERCHANT_ACCEPT', ref: 'POS-001', remark: null,                        system_log: 'Auto-accepted via merchant portal', created_at: '6 May 2026, 11:07 AM' },
    { id: 2, old_status: 'open',      status: 'open',      action: 'PAYMENT_SUCCESS', ref: 'TXN-884', remark: 'E-wallet payment confirmed', system_log: 'Payment webhook received',          created_at: '6 May 2026, 11:06 AM' },
    { id: 1, old_status: null,        status: 'open',      action: 'ORDER_CREATED',   ref: 'POS-001', remark: null,                        system_log: 'Order pushed to POS',               created_at: '6 May 2026, 11:03 AM' },
  ],
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const POS_STATUS_LABEL = { open: 'Open', closed: 'Closed', null: null }

function PaymentIcon({ icon }) {
  if (icon === 'wallet') return <Wallet size={20} />
  if (icon === 'bank')   return <Bank size={20} />
  return <CreditCard size={20} />
}

// ─── Cancel Order Modal (Portal) ──────────────────────────────────────────────

function CancelOrderModal({ order, onConfirm, onClose }) {
  const [reason, setReason] = useState(CANCEL_REASONS[0])
  const [note, setNote]     = useState('')

  return createPortal(
    <div className="cancel-modal-overlay" onClick={onClose}>
      <div className="cancel-modal" onClick={(e) => e.stopPropagation()}>

        <div className="cancel-modal__header">
          <h3>Cancel order {order.id}</h3>
          <button className="cancel-modal__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cancel-modal__body">
          <div className="cancel-modal__order-info">
            <span className="cancel-modal__customer">{order.customer}</span>
            <span className="cancel-modal__dot">·</span>
            <span className="cancel-modal__total">RM{order.total.toFixed(2)}</span>
          </div>
          <p className="cancel-modal__items-preview">
            {order.items.map((i) => `${i.name} x ${i.qty}`).join(', ')},
          </p>

          <label className="cancel-modal__label">Cancellation reason</label>
          <div className="cancel-modal__select-wrapper">
            <select
              className="cancel-modal__select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {CANCEL_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <CaretDown size={16} className="cancel-modal__select-caret" />
          </div>

          <label className="cancel-modal__label">
            Note to customer <span className="cancel-modal__optional">(optional)</span>
          </label>
          <textarea
            className="cancel-modal__textarea"
            rows={4}
            placeholder="Additional message sent with the cancellation notification"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="cancel-modal__footer">
          <button className="cancel-modal__keep" onClick={onClose}>Keep order open</button>
          <button
            className="cancel-modal__confirm"
            onClick={() => { onConfirm(reason, note); onClose() }}
          >
            <MinusCircle size={18} weight="fill" />
            Confirm cancellation
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Undo Toast ───────────────────────────────────────────────────────────────

function UndoToast({ toast, onUndo, onDismiss }) {
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(onDismiss, 5000)
    return () => clearTimeout(t)
  }, [toast, onDismiss])

  if (!toast) return null

  return (
    <div className="orders-toast">
      <CheckCircle size={18} weight="fill" className="orders-toast__icon" />
      <span className="orders-toast__message">{toast.message}</span>
      <button className="orders-toast__undo" onClick={onUndo}>Undo</button>
      <button className="orders-toast__close" onClick={onDismiss}><X size={16} /></button>
    </div>
  )
}

// ─── Revert Modal ─────────────────────────────────────────────────────────────

function RevertModal({ order, onConfirm, onCancel }) {
  const [reason, setReason] = useState('')
  const revertTo = REVERT_MAP[order?.status]
  if (!order || !revertTo) return null

  return (
    <div className="orders-settings-modal-overlay">
      <div className="orders-settings-modal">
        <div className="orders-settings-modal__header">
          <h3>Revert order status</h3>
          <button className="orders-settings-modal__close" onClick={onCancel}><X size={20} /></button>
        </div>
        <div className="orders-settings-modal__body">
          <div className="orders-revert-warning">
            <Warning size={18} weight="fill" />
            <p>This will revert <strong>{order.id}</strong> from <strong>{order.status}</strong> back to <strong>{revertTo}</strong>. This action is logged.</p>
          </div>
          <div className="orders-revert-reason">
            <label htmlFor="revert-reason">Reason <span>(required)</span></label>
            <textarea
              id="revert-reason"
              rows={3}
              placeholder="e.g. Accidentally marked as ready before food was prepared"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <div className="orders-settings-modal__footer">
          <button className="orders-settings-modal__cancel" onClick={onCancel}>Cancel</button>
          <button
            className="orders-settings-modal__save orders-settings-modal__save--danger"
            disabled={!reason.trim()}
            onClick={() => onConfirm(revertTo, reason.trim())}
          >
            Revert status
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

function AuditLog({ logs }) {
  if (!logs?.length) return null
  return (
    <div className="odp__section">
      <p className="odp__section-label">Activity log</p>
      <ul className="orders-audit-log">
        {logs.map((entry, i) => (
          <li key={i} className={`orders-audit-log__entry orders-audit-log__entry--${entry.type}`}>
            <span className="orders-audit-log__dot" />
            <div>
              <p className="orders-audit-log__text">{entry.message}</p>
              {entry.reason && <p className="orders-audit-log__reason">Reason: {entry.reason}</p>}
              <p className="orders-audit-log__time">{entry.time}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Status colour map ────────────────────────────────────────────────────────

const STATUS_COLOR = {
  pending:   'pending',
  submitted: 'submitted',
  paid:      'paid',
  confirmed: 'confirmed',
  ready:     'ready',
  completed: 'completed',
  cancelled: 'cancelled',
  open:      'open',
  closed:    'closed',
}

// ─── Order Status History ─────────────────────────────────────────────────────

function OrderStatusHistory({ history }) {
  if (!history?.length) return null
  return (
    <div className="odp__section">
      <p className="odp__section-label">Order status history</p>
      <div className="odp__history">
        {history.map((entry, i) => (
          <div key={entry.id} className="odp__history-row">
            {/* Timeline spine */}
            <div className="odp__history-spine">
              <span className={`odp__history-dot odp__history-dot--${STATUS_COLOR[entry.status] || 'default'}`} />
              {i < history.length - 1 && <span className="odp__history-line" />}
            </div>
            <div className="odp__history-content">
              <div className="odp__history-top">
                <span className={`odp__history-badge odp__history-badge--${STATUS_COLOR[entry.status] || 'default'}`}>
                  {entry.status}
                </span>
                <span className="odp__history-time">{entry.created_at}</span>
              </div>
              {entry.note && (
                <p className="odp__history-note">{entry.note}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── POS History ──────────────────────────────────────────────────────────────

const ACTION_LABEL = {
  ORDER_CREATED:   'Order created',
  PAYMENT_SUCCESS: 'Payment received',
  MERCHANT_ACCEPT: 'Merchant accepted',
  MARK_READY:      'Marked as ready',
  ORDER_COMPLETE:  'Order completed',
  ORDER_CANCEL:    'Order cancelled',
}

function PosHistory({ history }) {
  if (!history?.length) return null
  return (
    <div className="odp__section">
      <p className="odp__section-label">POS history</p>
      <div className="odp__history">
        {history.map((entry, i) => (
          <div key={entry.id} className="odp__history-row">
            <div className="odp__history-spine">
              <span className={`odp__history-dot odp__history-dot--${STATUS_COLOR[entry.status] || 'default'}`} />
              {i < history.length - 1 && <span className="odp__history-line" />}
            </div>
            <div className="odp__history-content">
              <div className="odp__history-top">
                <span className="odp__history-action">
                  {ACTION_LABEL[entry.action] || entry.action}
                </span>
                <span className="odp__history-time">{entry.created_at}</span>
              </div>
              <div className="odp__history-meta">
                {entry.old_status && (
                  <span className="odp__history-transition">
                    <span className={`odp__history-badge odp__history-badge--${STATUS_COLOR[entry.old_status] || 'default'}`}>
                      {entry.old_status}
                    </span>
                    <span className="odp__history-arrow">→</span>
                    <span className={`odp__history-badge odp__history-badge--${STATUS_COLOR[entry.status] || 'default'}`}>
                      {entry.status}
                    </span>
                  </span>
                )}
                {entry.ref && <span className="odp__history-ref">Ref: {entry.ref}</span>}
              </div>
              {entry.remark && <p className="odp__history-note">{entry.remark}</p>}
              {entry.system_log && <p className="odp__history-syslog">{entry.system_log}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Order Details Panel ──────────────────────────────────────────────────────

function OrderDetailsPanel({ order, auditLogs, statusHistory, posHistory, onClose, onAdvance, onRevert, onCancel, canRevert }) {
  const posLabel = order.pos_status === 'open' ? 'Open' : order.pos_status === 'closed' ? 'Closed' : null

  return (
    <div className="odp">
      {/* ── Top bar ── */}
      <div className="odp__topbar">
        <span className="odp__topbar-id">{order.id}</span>
        <button className="odp__topbar-close" onClick={onClose}><X size={18} /></button>
      </div>

      <div className="odp__body">
        {/* ── Title row ── */}
        <div className="odp__title-row">
          <h2 className="odp__id">{order.id}</h2>
          <div className="odp__badges">
            {posLabel && (
              <span className={`odp__badge odp__badge--pos odp__badge--pos-${order.pos_status}`}>
                <Circle size={8} weight="fill" /> {posLabel}
              </span>
            )}
            <span className={`odp__badge odp__badge--type odp__badge--type-${order.type.toLowerCase().replace('-', '')}`}>
              <Circle size={8} weight="fill" /> {order.type}
            </span>
          </div>
        </div>

        {/* ── Customer info ── */}
        <div className="odp__meta">
          <div className="odp__meta-row">
            <span className="odp__meta-key">Customer:</span>
            <span className="odp__meta-val odp__meta-val--bold">{order.customer}</span>
          </div>
          <div className="odp__meta-row">
            <span className="odp__meta-key">Phone number:</span>
            <span className="odp__meta-val">{order.phone}</span>
          </div>
          <div className="odp__meta-row">
            <span className="odp__meta-key">Points earned:</span>
            <span className="odp__meta-val">{order.points}pts</span>
          </div>
          <div className="odp__meta-row">
            <span className="odp__meta-key">Ordered at:</span>
            <span className="odp__meta-val">{order.orderedAt}</span>
          </div>
        </div>

        {/* ── Items ── */}
        <div className="odp__section">
          <p className="odp__section-label">ITEM(S)</p>
          <div className="odp__items">
            {order.items.map((item, i) => (
              <div key={i} className="odp__item">
                <div className="odp__item-left">
                  <span className="odp__item-qty">{item.qty}x</span>
                  <div>
                    <p className="odp__item-name">{item.name}</p>
                    {item.modifiers.map((m, j) => (
                      <p key={j} className="odp__item-modifier">{m}</p>
                    ))}
                  </div>
                </div>
                <span className="odp__item-price">{item.price.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Customer note */}
          {order.customerNote && (
            <div className="odp__customer-note">
              <Info size={16} className="odp__customer-note-icon" />
              <p><strong>Customer note:</strong> {order.customerNote}</p>
            </div>
          )}
        </div>

        {/* ── Pricing ── */}
        <div className="odp__pricing">
          <div className="odp__pricing-row">
            <span>Subtotal</span>
            <span>{order.subtotal.toFixed(2)}</span>
          </div>
          {order.voucher && (
            <div className="odp__pricing-row odp__pricing-row--discount">
              <span>{order.voucher.label}</span>
              <span>{order.voucher.amount.toFixed(2)}</span>
            </div>
          )}
          <div className="odp__pricing-row">
            <span>Service tax (10%)</span>
            <span>{order.serviceTax.toFixed(2)}</span>
          </div>
          <div className="odp__pricing-row">
            <span>SST (6%)</span>
            <span>{order.sst.toFixed(2)}</span>
          </div>
          <div className="odp__divider" />
          <div className="odp__pricing-row odp__pricing-row--total">
            <span>Total <span className="odp__incl-tax">(Incl. tax)</span></span>
            <span>RM{order.total.toFixed(2)}</span>
          </div>
        </div>

        {/* ── Payment ── */}
        <div className="odp__payment">
          <div className="odp__payment-icon">
            <PaymentIcon icon={order.payment.icon} />
          </div>
          <div className="odp__payment-info">
            <p className="odp__payment-method">{order.payment.method}</p>
            <p className="odp__payment-date">
              {order.payment.status === 'paid'
                ? `Paid on  ${order.payment.paidAt}`
                : order.payment.status === 'refunded'
                ? `Cancelled on  ${order.payment.paidAt}`
                : 'Awaiting payment'}
            </p>
          </div>
          <span className={`odp__payment-badge odp__payment-badge--${order.payment.status}`}>
            <Circle size={7} weight="fill" />
            {order.payment.status === 'paid' ? 'Paid'
              : order.payment.status === 'refunded' ? 'Refunded'
              : 'Pending'}
          </span>
        </div>

        {/* Order status history */}
        <OrderStatusHistory history={statusHistory} />

        {/* POS history */}
        <PosHistory history={posHistory} />

        {/* In-session activity log */}
        <AuditLog logs={auditLogs} />
      </div>

      {/* ── Footer ── */}
      <div className="odp__footer">
        <button
          className="odp__footer-cancel"
          onClick={onCancel}
        >
          Cancel order
        </button>

        <div className="odp__footer-actions">
          {canRevert && (
            <button className="odp__footer-revert" onClick={onRevert}>
              <ArrowCounterClockwise size={16} />
              Revert
            </button>
          )}
          {STATUS_FLOW[order.status]?.nextLabel && (
            <button className="odp__footer-primary" onClick={onAdvance}>
              {STATUS_FLOW[order.status].nextLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Filter config ────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: 'all',     label: 'All' },
  { value: 'dine-in', label: 'Dine-in' },
  { value: 'kiosk',   label: 'Kiosk' },
  { value: 'pickup',  label: 'Pickup' },
]

const DATE_OPTIONS = [
  { value: 'today',      label: 'Today' },
  { value: 'yesterday',  label: 'Yesterday' },
  { value: 'this-week',  label: 'This week' },
  { value: 'last-week',  label: 'Last week' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'custom',     label: 'Custom date…' },
]

const STATUS_OPTIONS = [
  { value: 'pending',   label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'paid',      label: 'Paid' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'ready',     label: 'Ready' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

// ─── Generic filter dropdown ──────────────────────────────────────────────────

function FilterDropdown({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const selected = options.find((o) => o.value === value)
  const isActive = value !== options[0].value

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className={`filter-drop ${open ? 'filter-drop--open' : ''} ${isActive ? 'filter-drop--active' : ''}`} ref={ref}>
      <button className="filter-drop__trigger" onClick={() => setOpen((v) => !v)}>
        <span className="filter-drop__label">{label}:</span>
        <span className="filter-drop__value">{selected?.label}</span>
        <CaretDown size={14} className="filter-drop__caret" />
      </button>

      {open && (
        <div className="filter-drop__menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              className={`filter-drop__option ${value === opt.value ? 'filter-drop__option--selected' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false) }}
            >
              {opt.label}
              {value === opt.value && <CheckCircle size={16} weight="fill" className="filter-drop__check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Date range dropdown with dual calendar ───────────────────────────────────

const MAX_RANGE_DAYS = 92 // ~3 months

function DateRangeDropdown({ value, customRange, onChange, onCustomRange }) {
  const [open, setOpen]           = useState(false)
  const [showCal, setShowCal]     = useState(false)
  // leftMonth is the month shown in the LEFT calendar (Date object, day=1)
  const [leftMonth, setLeftMonth] = useState(() => {
    const d = new Date(); d.setDate(1); return d
  })
  // Draft selection while user picks
  const [draft, setDraft]         = useState({ start: null, end: null, hover: null })
  const ref = useRef(null)

  const isCustom  = value === 'custom'
  const isActive  = value !== 'today'

  const rightMonth = new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1)

  // Trigger label
  const triggerLabel = (() => {
    if (isCustom && customRange.start && customRange.end) {
      const fmt = (d) => d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
      return `${fmt(customRange.start)} – ${fmt(customRange.end)}`
    }
    return DATE_OPTIONS.find((o) => o.value === value)?.label ?? 'Date range'
  })()

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setShowCal(false) } }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selectPreset = (opt) => {
    if (opt.value === 'custom') { setShowCal(true); setDraft({ start: null, end: null, hover: null }); return }
    onChange(opt.value)
    setOpen(false)
    setShowCal(false)
  }

  // ── Calendar helpers ──────────────────────────────────────────────────────

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay() // 0=Sun

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString()

  const isInRange = (day, start, end) => {
    if (!start || !end) return false
    const t = day.getTime()
    return t > Math.min(start.getTime(), end.getTime()) && t < Math.max(start.getTime(), end.getTime())
  }

  const isHoverRange = (day, start, hover) => {
    if (!start || !hover || draft.end) return false
    const t = day.getTime()
    return t > Math.min(start.getTime(), hover.getTime()) && t < Math.max(start.getTime(), hover.getTime())
  }

  const exceedsMax = (start, end) => {
    if (!start || !end) return false
    const diff = Math.abs(end - start) / (1000 * 60 * 60 * 24)
    return diff > MAX_RANGE_DAYS
  }

  const handleDayClick = (day) => {
    if (!draft.start || draft.end) {
      // Start fresh selection
      setDraft({ start: day, end: null, hover: null })
    } else {
      // Second click = end
      const start = draft.start
      const end   = day
      if (exceedsMax(start, end)) return // silently block
      const s = start < end ? start : end
      const e = start < end ? end   : start
      setDraft({ start: s, end: e, hover: null })
    }
  }

  const handleApply = () => {
    if (draft.start && draft.end) {
      onCustomRange({ start: draft.start, end: draft.end })
      onChange('custom')
      setOpen(false)
      setShowCal(false)
    }
  }

  const prevMonth = () => setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1))
  const nextMonth = () => setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1))

  const renderMonth = (baseDate) => {
    const year  = baseDate.getFullYear()
    const month = baseDate.getMonth()
    const days  = getDaysInMonth(year, month)
    const startDow = getFirstDayOfWeek(year, month)
    const monthLabel = baseDate.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })

    const cells = []
    for (let i = 0; i < startDow; i++) cells.push(null)
    for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d))

    const today = new Date(); today.setHours(0,0,0,0)

    // Max end date from draft.start
    const maxEnd = draft.start && !draft.end
      ? new Date(draft.start.getTime() + MAX_RANGE_DAYS * 86400000)
      : null

    return (
      <div className="drcal__month">
        <div className="drcal__month-label">{monthLabel}</div>
        <div className="drcal__weekdays">
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d) => (
            <span key={d} className="drcal__wd">{d}</span>
          ))}
        </div>
        <div className="drcal__grid">
          {cells.map((day, i) => {
            if (!day) return <span key={`e${i}`} />

            const isStart   = isSameDay(day, draft.start)
            const isEnd     = isSameDay(day, draft.end)
            const inRange   = isInRange(day, draft.start, draft.end)
            const inHover   = isHoverRange(day, draft.start, draft.hover)
            const isToday   = isSameDay(day, today)
            const disabled  = maxEnd ? day > maxEnd : false

            return (
              <button
                key={day.toISOString()}
                className={[
                  'drcal__day',
                  isStart  ? 'drcal__day--start'    : '',
                  isEnd    ? 'drcal__day--end'       : '',
                  inRange  ? 'drcal__day--in-range'  : '',
                  inHover  ? 'drcal__day--hover-range': '',
                  isToday  ? 'drcal__day--today'     : '',
                  disabled ? 'drcal__day--disabled'  : '',
                ].filter(Boolean).join(' ')}
                onClick={() => !disabled && handleDayClick(day)}
                onMouseEnter={() => draft.start && !draft.end && setDraft((p) => ({ ...p, hover: day }))}
                disabled={disabled}
              >
                {day.getDate()}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={`filter-drop ${open ? 'filter-drop--open' : ''} ${isActive ? 'filter-drop--active' : ''}`} ref={ref}>
      <button className="filter-drop__trigger" onClick={() => { setOpen((v) => !v); if (!open) setShowCal(isCustom) }}>
        <span className="filter-drop__label">Date range:</span>
        <span className="filter-drop__value">{triggerLabel}</span>
        <CaretDown size={14} className="filter-drop__caret" />
      </button>

      {open && !showCal && (
        <div className="filter-drop__menu">
          {DATE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`filter-drop__option ${value === opt.value ? 'filter-drop__option--selected' : ''}`}
              onClick={() => selectPreset(opt)}
            >
              {opt.label}
              {value === opt.value && <CheckCircle size={16} weight="fill" className="filter-drop__check" />}
            </button>
          ))}
        </div>
      )}

      {open && showCal && (
        <div className="drcal">
          <div className="drcal__header">
            <button className="drcal__nav" onClick={prevMonth}><CaretDown size={14} style={{ transform: 'rotate(90deg)' }} /></button>
            <span />
            <button className="drcal__nav" onClick={nextMonth}><CaretDown size={14} style={{ transform: 'rotate(-90deg)' }} /></button>
          </div>

          <div className="drcal__calendars">
            {renderMonth(leftMonth)}
            {renderMonth(rightMonth)}
          </div>

          <div className="drcal__footer">
            <span className="drcal__hint">
              {!draft.start
                ? 'Select start date'
                : !draft.end
                ? 'Select end date (max 3 months)'
                : `${draft.start.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })} – ${draft.end.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}`
              }
            </span>
            <div className="drcal__footer-actions">
              <button className="drcal__back" onClick={() => { setShowCal(false); setDraft({ start: null, end: null, hover: null }) }}>
                ← Back
              </button>
              <button
                className="drcal__apply"
                disabled={!draft.start || !draft.end}
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Status multi-select filter ───────────────────────────────────────────────

function StatusFilterDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  // value is a Set of selected statuses; empty = all
  const count = value.size

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggle = (status) => {
    const next = new Set(value)
    if (next.has(status)) next.delete(status)
    else next.add(status)
    onChange(next)
  }

  const clearAll = () => onChange(new Set())

  return (
    <div className={`filter-drop ${open ? 'filter-drop--open' : ''} ${count > 0 ? 'filter-drop--active' : ''}`} ref={ref}>
      <button className="filter-drop__trigger" onClick={() => setOpen((v) => !v)}>
        <FunnelSimple size={14} />
        <span className="filter-drop__value">
          Status{count > 0 ? ` (${count})` : ''}
        </span>
        <CaretDown size={14} className="filter-drop__caret" />
      </button>

      {open && (
        <div className="filter-drop__menu filter-drop__menu--status">
          <div className="filter-drop__menu-header">
            <span>Filter by status</span>
            {count > 0 && (
              <button className="filter-drop__clear" onClick={clearAll}>Clear all</button>
            )}
          </div>
          {STATUS_OPTIONS.map((opt) => {
            const checked = value.has(opt.value)
            return (
              <button
                key={opt.value}
                className={`filter-drop__option filter-drop__option--check ${checked ? 'filter-drop__option--selected' : ''}`}
                onClick={() => toggle(opt.value)}
              >
                <span className={`filter-drop__checkbox ${checked ? 'filter-drop__checkbox--checked' : ''}`}>
                  {checked && <CheckCircle size={12} weight="fill" />}
                </span>
                <span className={`filter-drop__status-dot filter-drop__status-dot--${opt.value}`} />
                {opt.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OrdersPage() {
  const [orders, setOrders] = useState(INITIAL_ORDERS)
  const [showOrderSettings, setShowOrderSettings] = useState(false)
  const [autoAcceptOrders, setAutoAcceptOrders] = useState(true)

  // Filters
  const [filterType,    setFilterType]    = useState('all')
  const [filterDate,    setFilterDate]    = useState('today')
  const [customRange,   setCustomRange]   = useState({ start: null, end: null })
  const [filterStatus,  setFilterStatus]  = useState(new Set())
  const [searchQuery,   setSearchQuery]   = useState('')

  const [openMenuId, setOpenMenuId] = useState(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })

  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showOrderDetails, setShowOrderDetails] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)

  const [toast, setToast] = useState(null)
  const undoSnapshot = useRef(null)

  const [showRevertModal, setShowRevertModal] = useState(false)
  const [auditLogs, setAuditLogs] = useState({})

  // ── Derived filtered list ─────────────────────────────────────────────────
  const filteredOrders = orders.filter((o) => {
    if (filterType !== 'all' && o.type.toLowerCase() !== filterType) return false
    if (filterStatus.size > 0 && !filterStatus.has(o.status)) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      const itemStr = o.items.map((i) => i.name).join(' ').toLowerCase()
      if (!o.id.toLowerCase().includes(q) && !o.customer.toLowerCase().includes(q) && !itemStr.includes(q)) return false
    }
    return true
  })

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.orders-table__actions')) setOpenMenuId(null)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (selectedOrder) {
      const updated = orders.find((o) => o.id === selectedOrder.id)
      if (updated) setSelectedOrder(updated)
    }
  }, [orders])

  const handleToggleMenu = (e, orderId) => {
    e.stopPropagation()
    if (openMenuId === orderId) { setOpenMenuId(null); return }
    const rect = e.currentTarget.getBoundingClientRect()
    const top  = (window.innerHeight - rect.bottom >= 170) ? rect.bottom + 8 : rect.top - 170 - 8
    const left = rect.right - 240
    setDropdownPos({ top, left })
    setOpenMenuId(orderId)
  }

  const nowTime = () => new Date().toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })

  const pushLog = (orderId, entry) =>
    setAuditLogs((prev) => ({ ...prev, [orderId]: [entry, ...(prev[orderId] || [])] }))

  const applyStatus = (orderId, newStatus, message, type = 'advance', reason = null) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)))
    pushLog(orderId, { message, reason, type, time: nowTime() })
  }

  const handleAdvanceStatus = (order) => {
    const flow = STATUS_FLOW[order.status]
    if (!flow?.next) return
    const snapshot = [...orders]
    undoSnapshot.current = { snapshot, orderId: order.id, prevStatus: order.status }
    applyStatus(order.id, flow.next, `Status changed: "${order.status}" → "${flow.next}"`, 'advance')
    setToast({ message: `${order.id} marked as ${flow.next}` })
  }

  const handleUndo = () => {
    if (!undoSnapshot.current) return
    const { snapshot, orderId, prevStatus } = undoSnapshot.current
    setOrders(snapshot)
    pushLog(orderId, { message: `Status reverted to "${prevStatus}" via undo`, type: 'undo', time: nowTime() })
    setToast(null)
    undoSnapshot.current = null
  }

  const handleRevertConfirm = (revertTo, reason) => {
    applyStatus(selectedOrder.id, revertTo, `[Manager] Reverted: "${selectedOrder.status}" → "${revertTo}"`, 'revert', reason)
    setShowRevertModal(false)
  }

  const handleCancelConfirm = (reason, note) => {
    applyStatus(selectedOrder.id, 'cancelled', `Order cancelled — ${reason}${note ? `: "${note}"` : ''}`, 'cancel')
    setShowCancelModal(false)
  }

  const canRevert = selectedOrder && !PAYMENT_LOCKED.has(selectedOrder?.status) && !!REVERT_MAP[selectedOrder?.status]

  return (
    <div className="orders-page">
      <div className="orders-page__content">

        {/* HEADER */}
        <div className="orders-header">
          <div>
            <div className="orders-header__breadcrumb">Hikaya House / Orders</div>
            <h1 className="orders-header__title">Orders</h1>
            <p className="orders-header__description">Auto-accept active — orders confirmed instantly</p>
          </div>
          <div className="orders-header__actions">
            <button className="orders-btn orders-btn--secondary" onClick={() => setShowOrderSettings(true)}>
              <Gear size={18} /> Order settings
            </button>
            <button className="orders-btn">
              <DownloadSimple size={18} /> Export CSV
            </button>
            {showOrderSettings && (
              <div className="orders-settings-modal-overlay">
                <div className="orders-settings-modal">
                  <div className="orders-settings-modal__header">
                    <h3>Order settings</h3>
                    <button className="orders-settings-modal__close" onClick={() => setShowOrderSettings(false)}><X size={20} /></button>
                  </div>
                  <div className="orders-settings-modal__body">
                    <div className="orders-settings-modal__row">
                      <div>
                        <h4>Auto-accept orders</h4>
                        <p>Orders confirmed automatically without manual approval</p>
                      </div>
                      <label className="orders-switch">
                        <input type="checkbox" checked={autoAcceptOrders} onChange={(e) => setAutoAcceptOrders(e.target.checked)} />
                        <span className="orders-switch__slider"></span>
                      </label>
                    </div>
                  </div>
                  <div className="orders-settings-modal__footer">
                    <button className="orders-settings-modal__cancel" onClick={() => setShowOrderSettings(false)}>Cancel</button>
                    <button className="orders-settings-modal__save" onClick={() => setShowOrderSettings(false)}>Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* STATS */}
        <div className="orders-stats">
          {stats.map((stat) => (
            <div className="orders-stat-card" key={stat.label}>
              <div className="orders-stat-card__label">{stat.label}</div>
              <div className="orders-stat-card__value">{stat.value}</div>
            </div>
          ))}
        </div>

        {/* TABS */}
        <div className="orders-tabs">
          <button className="active">All orders</button>
          <button>Open (5)</button>
          <button>Closed (7)</button>
        </div>

        {/* FILTERS */}
        <div className="orders-toolbar">
          <div className="orders-toolbar__filters">
            <FilterDropdown
              label="Type"
              value={filterType}
              options={TYPE_OPTIONS}
              onChange={setFilterType}
            />
            <DateRangeDropdown
              value={filterDate}
              customRange={customRange}
              onChange={setFilterDate}
              onCustomRange={setCustomRange}
            />
            <StatusFilterDropdown
              value={filterStatus}
              onChange={setFilterStatus}
            />
          </div>
          <div className="orders-search">
            <MagnifyingGlass size={18} />
            <input type="text" placeholder="Search orders" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>

        {/* TABLE */}
        <div className="orders-table-wrapper">
          <table className="orders-table">
            <colgroup>
              <col style={{ width: '180px' }} /><col />
              <col style={{ width: '150px' }} /><col style={{ width: '150px' }} />
              <col style={{ width: '130px' }} /><col style={{ width: '130px' }} />
              <col style={{ width: '130px' }} /><col style={{ width: '50px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>ORDER ID</th><th>ITEMS</th><th>TYPE</th><th>TOTAL</th>
                <th>PLACED</th><th>CLOSED</th><th>STATUS</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td className="orders-table__id">{order.id}</td>
                  <td className="orders-table__items">
                    <p className="orders-table__items-text">
                      {order.items.map((i) => `${i.name} x ${i.qty}`).join(', ')}
                    </p>
                  </td>
                  <td>
                    <span className={`orders-tag orders-tag--${order.type.toLowerCase().replace('-','')}`}>{order.type}</span>
                  </td>
                  <td>RM{order.total.toFixed(2)}</td>
                  <td>{order.placed}</td>
                  <td>{order.closed}</td>
                  <td>
                    <span className={`orders-table__status orders-table__status--${order.status}`}>{order.status}</span>
                  </td>
                  <td>
                    <div className="orders-table__actions">
                      <button className="orders-table__menu-button" onClick={(e) => handleToggleMenu(e, order.id)}>
                        <DotsThreeVertical size={18} />
                      </button>
                      {openMenuId === order.id && (
                        <div className="orders-table__dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
                          <button className="orders-table__dropdown-item" onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); setShowOrderDetails(true); setOpenMenuId(null) }}>
                            <ClipboardText size={18} /> View details
                          </button>
                          <button className="orders-table__dropdown-item">
                            <Receipt size={18} /> Print receipt
                          </button>
                          <button className="orders-table__dropdown-item orders-table__dropdown-item--danger" onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); setShowCancelModal(true); setOpenMenuId(null) }}>
                            <MinusCircle size={18} /> Cancel order
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* PAGINATION */}
        <div className="orders-pagination">
          <div>Showing 1-10 of 79 results</div>
          <div className="orders-pagination__pages">
            <button className="active">1</button>
            <button>2</button><button>3</button><button>4</button><button>5</button>
            <span>...</span><button>8</button>
          </div>
        </div>
      </div>
      
      {/* ORDER DETAILS PANEL */}
      {showOrderDetails && selectedOrder && (
        <OrderDetailsPanel
          order={selectedOrder}
          auditLogs={auditLogs[selectedOrder.id]}
          statusHistory={ORDER_STATUS_HISTORIES[selectedOrder.id] || []}
          posHistory={ORDER_POS_HISTORIES[selectedOrder.id] || []}
          onClose={() => setShowOrderDetails(false)}
          onAdvance={() => handleAdvanceStatus(selectedOrder)}
          onRevert={() => setShowRevertModal(true)}
          onCancel={() => setShowCancelModal(true)}
          canRevert={canRevert}
        />
      )}

      {/* CANCEL MODAL */}
      {showCancelModal && selectedOrder && (
        <CancelOrderModal
          order={selectedOrder}
          onConfirm={handleCancelConfirm}
          onClose={() => setShowCancelModal(false)}
        />
      )}

      {/* REVERT MODAL */}
      {showRevertModal && (
        <RevertModal order={selectedOrder} onConfirm={handleRevertConfirm} onCancel={() => setShowRevertModal(false)} />
      )}

      {/* UNDO TOAST */}
      <UndoToast toast={toast} onUndo={handleUndo} onDismiss={() => setToast(null)} />
    </div>
  )
}